import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import CommentListItemAttaches from './CommentListItemAttaches';
import InlineUserRepresentation from './InlineUserRepresentation';
import classnames from '@src/helpers/classnames';
import CommentListItemControls from './CommentListItemControls';
import {IReduxRootState} from '@src/@interfaces';
import useIntersectionObserver from '@src/hooks/useIntersectionObserver';
import {markCommentAsRead} from '@src/_redux/features/markable-as-read/actions';
import RenderIf from '@src/components/RenderIf/RenderIf';
import Translate from '@src/components/Translate/Translate';
import useUserId from '@src/hooks/useUserId';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans from '@src/helpers/trans';
import MomentDate from '@src/components/MomentDate/MomentDate';
import {moment} from '@src/helpers/dates';
import BlockquoteOfComment from '@src/components/CommentsList/BlockquoteOfComment/BlockquoteOfComment';

const COMMENT_MESSAGE__MAX_LINE_BREAKS_AT_ROW = 3;

export const prepareMessage = ((maxLineBreaksAtRow: number) => {
    const regExp = new RegExp(`(\\r\\n|\\n|\\r){${maxLineBreaksAtRow},}`, 'mug');
    const replacement = Array.from(
            {length: maxLineBreaksAtRow},
            () => '\r\n'
    ).join('');

    return (message: string | undefined | null) => message?.replace(regExp, replacement);
})(COMMENT_MESSAGE__MAX_LINE_BREAKS_AT_ROW);

export default function CommentInline(props: {
    model: ICommentModel,
    allowToReply?: boolean,
    allowToDelete?: boolean,
    allowHighlight?: boolean,
    onReply?: (comment: ICommentModel) => void,
    onDeleted?: (comment: ICommentModel) => void,
    onBlockquoteClicked?: (comment: ICommentModel) => void,
    size?: 'sm',
    intersectionObserver?: ReturnType<typeof useIntersectionObserver>,
}) {
    const [searchParams] = useSearchParams();

    const comment = props.model;
    const message = comment.message?.length ? prepareMessage(comment.message) : null;

    const currentUserId = useUserId();
    const {locale} = useContext(LocaleContext);
    const [isManuallyDeleted, setIsManuallyDeleted] = useState(false);

    const baseOnDeleted = props.onDeleted;
    const baseOnBlockquoteClicked = props.onBlockquoteClicked;
    const {onDeleted, onBlockquoteClicked} = useMemo(() => {
        const onDeleted = () => {
            if (baseOnDeleted) {
                baseOnDeleted(comment);
            }

            setIsManuallyDeleted(true);
        };

        const onBlockquoteClicked = baseOnBlockquoteClicked ? () => {
            baseOnBlockquoteClicked(comment);
        } : undefined;

        return {onDeleted, onBlockquoteClicked};
    }, [baseOnDeleted, baseOnBlockquoteClicked, comment]);

    const isDeleted = isManuallyDeleted || comment.deleted_at != null;

    const readCommentsIds = useSelector(
            (state: IReduxRootState) => state.features.markableAsRead.readCommentsIds
    );

    const amIAuthor = comment.creator_id === currentUserId;
    const isReadByOthers = amIAuthor && comment.read_marks?.some(
            mark => mark.is_read && mark.user_id !== currentUserId
    ) === true;

    const haveToBeMarkedAsRead = (
            !amIAuthor &&
            !(comment.id in readCommentsIds) &&
            (
                    ('read_mark' in comment) && !comment.read_mark?.is_read ||
                    ('read_marks' in comment) && !comment.read_marks?.some(
                            mark => mark.is_read && mark.user_id === currentUserId
                    )
            )
    );

    const ref = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();
    const {intersectionObserver} = props;

    useEffect(() => {
        if (!intersectionObserver || !haveToBeMarkedAsRead || !ref.current || !comment?.id) {
            return;
        }

        const observation = intersectionObserver.observe(ref.current);
        const subscription = intersectionObserver.subscribe(ref.current, () => {
            setTimeout(() => {
                dispatch(markCommentAsRead(comment.id));
            }, 300);
        }, true);

        return () => {
            observation.unobserve();
            subscription.unsubscribe();
        };
    }, [haveToBeMarkedAsRead, intersectionObserver, comment?.id, dispatch]);

    const createdAt = comment.created_at ? moment(comment.created_at) : null;

    return <div className={classnames('comment-list__item', 'comment-list__item--inline', {
        [`comment-list__item--${props.size}`]: props.size,
        'comment-list__item--without-message': !message,
        'comment-list__item--without-author': !comment.creator,
        'comment-list__item--iam-author': comment.creator_id === currentUserId,
        'comment-list__item--with-attaches': comment.files?.length,
        'comment-list__item--with-message': comment.message?.length,
        'comment-list__item--unread': haveToBeMarkedAsRead,
        'comment-list__item--deleted': isDeleted,
        'comment-list__item--highlighted': (
                props.allowHighlight !== false &&
                comment.id != null &&
                searchParams.get('commentId') === String(comment.id)
        ),
    })} ref={ref}>
        <div className="comment-list__item__body">
            <div className="comment-list__item__head">
                <InlineUserRepresentation
                        user={comment?.creator}
                        tag="div"
                        className="comment-list__item__author"
                />

                <div className="comment-list__item__head__info">
                    {isDeleted ? null : <CommentListItemControls {...props} onDeleted={onDeleted}/>}
                </div>
            </div>

            <BlockquoteOfComment comment={comment} onClick={onBlockquoteClicked}/>

            {(message && !isDeleted) ? <div className="comment-list__item__message">{message}</div> : null}
            {isDeleted ? <Translate
                    message="Chat.this-message-has-been-deleted-message"
                    tag="div"
                    className="comment-list__item__message"
            /> : null}

            <RenderIf expression={!isDeleted}>
                <CommentListItemAttaches
                        list={comment.files}
                        onDownload={file => {
                            window.location.assign(file.asset_file_path);
                        }}
                />
            </RenderIf>

            <div className="comment-list__item__footer">
                <RenderIf expression={amIAuthor}>
                    <div className={classnames('comment-list__item__read-mark', {
                        'comment-list__item__read-mark--read': isReadByOthers,
                    })}>
                        <i
                                className={classnames('fas', {
                                    'fa-check': !isReadByOthers,
                                    'fa-check-double': isReadByOthers,
                                })}
                                title={
                                    isReadByOthers ?
                                            trans('Chat.this-message-has-been-read', locale) :
                                            trans('Chat.this-message-has-not-been-read', locale)
                                }
                        />
                    </div>
                </RenderIf>

                <RenderIf expression={createdAt}>
                    <div className="comment-list__item__created_at" title={createdAt?.format('LLL')}>
                        <MomentDate date={createdAt as any} calendarSameElse="DD MMM YYYY LT"/>
                    </div>
                </RenderIf>
            </div>
        </div>
    </div>;
}
